import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { VoucherSearch } from '../models/vouchers/voucher-search';
import { IFileTransfer } from '../models/common/file.transfer';
import { environment } from '@env/environment';
import { ToastrService } from 'ngx-toastr';
import { IVoucherDashboardViewModel } from '../models/vouchers/voucher-dashboard.viewmodel';
import { IVoucherSearchResult } from '../models/vouchers/voucher.search.result';
import { Voucher } from '../models/vouchers/voucher';
import { VoucherLetter } from '../models/vouchers/voucher-letter';
import { FormControl, FormGroup } from '@angular/forms';
import { VoucherApplication } from '../models/vouchers/voucher-application';
import { VoucherLetterUpload } from '../models/vouchers/voucher-letter-upload';
import { ParameterResultLetter } from '../models/well/samples/parameterResultLetter.viewmodel';
import { IBulkUploadSample } from 'app/models/bulkUploads/bulk.upload.sample';
import { VoucherReportNitrateExceedance } from 'app/models/vouchers/voucher-report-nitrate-exceedance';
import { VoucherReport } from 'app/models/vouchers/voucher-report';
import { VoucherLetterEmailViewModel } from 'app/models/vouchers/voucher-letter-email-viewmodel';
import { IFuzzySearchResult } from 'app/models/common/fuzzySearchResult';
import { ISearchResult } from '../models/well/search/search.result';
import { ITreatmentViewModel } from 'app/models/household/treatment';
import { SampleViewModel } from 'app/models/well/samples/sample.viewmodel';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  }),
  withCredentials: true
};

const httpOptionsForFiles = {
  withCredentials: true
};

@Injectable({
  providedIn: 'root',
})
export class VoucherService {
  issued: string = "Issued";
  duplicate: string = "Duplicate";
  waitlist: string = "Waitlist";
  notEligible: string = "Not Eligible";
  new: string = "New";
  email: string = "Email";
  phone: string = "Phone";
  mail: string = "Mail";
  voucherLetterType = "Voucher";
  voucherCOCLetterType = "Chain of Custody";
  lubgwmaTreatmentLetterType = "Treatment";
  voucherScheduleLetterType = "Voucher Schedule";
  voucherSelfSampleLetterType = "Voucher Self-Sample";
  exceedanceLetterType = "Exceedance";
  lubgwmaType = "LUBGWMA";
  lubgwmaTestingVoucherType = "LUBGWMA Testing";
  lubgwmaTreatmentVoucherType = "LUBGWMA Treatment";
  lubgwmatestingSurvey123VoucherType = "LUBGWMA Testing - Survey 123";
  wildfireVoucherType = "Wildfire 2020";
  voucherTypes: string[] = [this.wildfireVoucherType, this.lubgwmaTestingVoucherType, this.lubgwmatestingSurvey123VoucherType, this.lubgwmaTreatmentVoucherType];
  voucherTypesForNewApplications: string[] = [this.lubgwmaTestingVoucherType, this.lubgwmaTreatmentVoucherType];
  voucherTypesForImport: string[] = [this.lubgwmaTestingVoucherType, this.lubgwmatestingSurvey123VoucherType];
  voucherTypesForUploadResults: string[] = [this.wildfireVoucherType, this.lubgwmaTestingVoucherType, this.lubgwmaTreatmentVoucherType];
  preferredSampleDays: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  preferredSampleTimes: string[] = ['Morning', 'Afternoon', 'Evening', 'No_Preference'];
  applicationSources: string[] = ['Public', 'Canvas'];
  voucherUploadType = "Voucher";
  wellUploadType = "Well";
  other = "Other";
  EnglishOnly = "English only";
  preferNotToanswer = "Prefer not to answer";
  Yes = "Yes";
  No = "No";
  errorMessages = [];
  statuses: string[] = [this.issued, this.duplicate, this.waitlist, this.new];
  voucherStatuses: string[] = [this.issued, this.waitlist, this.duplicate];
  types: string[] = [this.wildfireVoucherType, this.lubgwmaTestingVoucherType, this.lubgwmaTreatmentVoucherType];
  contactMethods: string[] = [this.email, this.phone, this.mail];
  privateWellText = "Yes, the well is private. I own or rent the property and the well.";
  publicWellText = "No, the well is part of a public water system. I pay a water bill."

  wildfireList: string[] = [
    'Alameda Fire',
    'Archie Creek Fire',
    'Beachie Creek Fire',
    'Echo Mountain Complex',
    'Holiday Farm Fire',
    'Lionshead Fire',
    'Riverside Fire',
    'Slater Fire',
    'Two Four Two Fire',
    'Unsure'
  ];
  lubgwmaVoucherExpiration: string = "June 7, 2023";
  wildfireVoucherExpiration: string = "June 7, 2023";

  genericOptions: string[] =
    [
      "Yes",
      "No",
      "Don't know",
      "Don't want to answer",
      "Don't know what this question is asking"
    ];

  wellDamageOptions: string[] =
    [
      "Yes",
      "No",
      "Unsure"
    ];

  preferredContactTypeOptions: string[] =
    [
      "Email",
      "Mail",
      "Phone"
    ];

  householdsServedOptions: string[] =
    [
      "1 household",
      "2 households",
      "3 households"
    ];

  primaryResidenceOptions: string[] =
    [
      "Yes, this is the primary residence for my household or for full-time tenants",
      "No, this is used as a second residence.",
      "No, this is a vacation or rental property."
    ];

  search: VoucherSearch = {
    voucherId: '',
    voucherApplicationId: '',
    applicationIdentifier: '',
    wellId: 0,
    testingNeed: '',
    contact: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    county: '',
    email: '',
    phone: '',
    redeemed: false,
    exceedencesDetected: false,
    status: '',
    wellTag: '',
    wellLog: '',
    receivedStartDate: '',
    receivedEndDate: '',
    type: '',
    includeLetters: false
  }

  searchForm = new FormGroup({
    voucherId: new FormControl(),
    applicationId: new FormControl(),
    county: new FormControl(),
    address: new FormControl(),
    city: new FormControl(),
    zip: new FormControl(),
    wellTag: new FormControl(),
    wellLog: new FormControl(),
    applicationIdentifier: new FormControl(),
    status: new FormControl(),
    type: new FormControl(),
    contact: new FormControl(),
    exceedencesDetected: new FormControl(false),
    redeemed: new FormControl(false),
    receivedStartDate: new FormControl(),
    receivedEndDate: new FormControl(),
  });

  haveSearchResults = false;
  searchResultsCount = 0;
  searchResults: IFuzzySearchResult[] = [];

  ohaWebsite: string = 'OHA website';
  otherWebsite: string = 'Other website';
  ohaPressRelease: string = 'OHA press release';
  communityEvent: string = 'Community event (fair, music festival, farmer\'s market)';
  facebook: string = 'Facebook post/group';
  twitter: string = 'Twitter post';
  radio: string = 'Radio';
  newspaper: string = 'Newspaper';
  countyPublicHealth: string = 'County Public Health Department';
  church: string = 'Church';
  workplace: string = 'Workplace';
  tribalHealth: string = 'Tribal health';
  groceryStore: string = 'Grocery store, pharmacy';
  searchEngine: string = 'Search Engine';


  // #region Race - Ethnicity

  centralAmerican = 'Hispanic and Latino/a/x - Central American';
  mexican = 'Hispanic and Latino/a/x - Mexican';
  southAmerican = 'South American';
  otherLatinx = 'Hispanic and Latino/a/x - Other Hispanic or Latino/a/x';
  chamoru = 'Native Hawaiian and Pacific Islander - CHamoru (Chamorro)';
  marshallese = 'Native Hawaiian and Pacific Islander - Marshallese';
  micronesian = 'Native Hawaiian and Pacific Islander - Communities of the Micronesian Region';
  nativeHawaiian = 'Native Hawaiian and Pacific Islander - Native Hawaiian';
  samoan = 'Native Hawaiian and Pacific Islander - Samoan';
  otherPacificIslander = 'Native Hawaiian and Pacific Islander - Other Pacific Islander';
  easternEuropean = 'White - Eastern European';
  slavic = 'White- Slavic';
  westernEuropean = 'White - Western European';
  otherWhite = 'White - Other White';
  americanIndian = 'American Indian and Alaska Native - American Indian';
  alaskaNative = 'American Indian and Alaska Native - Alaska Native';
  canadianFirstNation = 'American Indian and Alaska Native - Canadian Inuit, Metis, or First Nation';
  indigenousCentralAmerican = 'American Indian and Alaska Native - Indigenous Mexican, Central American, or South American.';
  africanAmerican = 'Black and African American - African American';
  afroCarribbean = 'Black and African American - Afro-Caribbean';
  ethiopian = 'Black and African American - Ethiopian';
  somali = 'Black and African American - Somali';
  otherAfrican = 'Black and African American - Other African (Black)';
  otherBlack = 'Black and African American - Other Black';
  middleEastern = 'Middle Eastern/North African - Middle Eastern';
  northAfrican = 'Middle Eastern/North African - North African';
  asianIndian = 'Asian - Asian Indian';
  cambodian = 'Asian - Cambodian';
  chinese = 'Asian - Chinese';
  myanmar = 'Asian - Communities of Myanmar';
  filipino = 'Asian - Filipino/a';
  hmong = 'Asian - Hmong';
  japanese = 'Asian - Japanese';
  korean = 'Asian - Korean';
  laotian = 'Asian - Laotian';
  southAsian = 'Asian - South Asian';
  vietnamese = 'Asian - Vietnamese';
  otherAsian = 'Asian - Other Asian';
  dontKnow = 'Don’t know';
  raceEthDecline = 'Don’t want to answer';
  raceEthOther = 'Other';

  primaryRaceEthnicityList: Array<{ id: string, value: string }> = [
    { id: 'centralAmerican', value: this.centralAmerican },
    { id: 'mexican', value: this.mexican },
    { id: 'southAmerican', value: this.southAmerican },
    { id: 'otherLatinx', value: this.otherLatinx },
    { id: 'chamoru', value: this.chamoru },
    { id: 'marshallese', value: this.marshallese },
    { id: 'micronesian', value: this.micronesian },
    { id: 'nativeHawaiian', value: this.nativeHawaiian },
    { id: 'samoan', value: this.samoan },
    { id: 'otherPacificIslander', value: this.otherPacificIslander },
    { id: 'easternEuropean', value: this.easternEuropean },
    { id: 'slavic', value: this.slavic },
    { id: 'westernEuropean', value: this.westernEuropean },
    { id: 'otherWhite', value: this.otherWhite },
    { id: 'americanIndian', value: this.americanIndian },
    { id: 'alaskaNative', value: this.alaskaNative },
    { id: 'canadianFirstNation', value: this.canadianFirstNation },
    { id: 'indigenousCentralAmerican', value: this.indigenousCentralAmerican },
    { id: 'africanAmerican', value: this.africanAmerican },
    { id: 'afroCarribbean', value: this.afroCarribbean },
    { id: 'ethiopian', value: this.ethiopian },
    { id: 'somali', value: this.somali },
    { id: 'otherAfrican', value: this.otherAfrican },
    { id: 'otherBlack', value: this.otherBlack },
    { id: 'middleEastern', value: this.middleEastern },
    { id: 'northAfrican', value: this.northAfrican },
    { id: 'asianIndian', value: this.asianIndian },
    { id: 'cambodian', value: this.cambodian },
    { id: 'chinese', value: this.chinese },
    { id: 'myanmar', value: this.myanmar },
    { id: 'filipino', value: this.filipino },
    { id: 'hmong', value: this.hmong },
    { id: 'japanese', value: this.japanese },
    { id: 'korean', value: this.korean },
    { id: 'laotian', value: this.laotian },
    { id: 'southAsian', value: this.southAsian },
    { id: 'vietnamese', value: this.vietnamese },
    { id: 'otherAsian', value: this.otherAsian },
    { id: 'dontKnow', value: this.dontKnow },
    { id: 'raceEthDecline', value: this.raceEthDecline },
    { id: 'raceEthOther', value: this.raceEthOther }
  ];

  primaryRaceEthnicityTextList: string[] =
    [
      this.centralAmerican,
      this.mexican,
      this.southAmerican,
      this.filipino
    ];


  // #endregion

  constructor(private toastr: ToastrService, private http?: HttpClient) { }

  // #region Actions

  Search(search: VoucherSearch): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/Search', search, httpOptions);
  }

  DownloadCsv(search: VoucherSearch): Observable<any> {
    this.search = search;
    return this.http.post(environment.privateApi + 'Voucher/DownloadCsv', search, httpOptions);
  }

  DownloadPdf(html: IFileTransfer): Observable<IFileTransfer> {
    return this.http.post<any>(environment.privateApi + 'Voucher/DownloadPdf', html, httpOptions);
  }

  getVoucherDashboard(county: string): Observable<IVoucherDashboardViewModel> {
    return this.http.get<IVoucherDashboardViewModel>(environment.privateApi + 'Voucher/dashboard/' + county, { withCredentials: true });
  }

  getVoucherById(voucherId: number): Observable<Voucher> {
    return this.http.get<Voucher>(environment.privateApi + 'Voucher/' + voucherId, { withCredentials: true });
  }

  getVouchersByWellId(wellId: number): Observable<Voucher[]> {
    return this.http.get<Voucher[]>(environment.privateApi + 'Voucher/Well/' + wellId, { withCredentials: true });
  }

  getVoucherApplicationById(applicationId: number): Observable<VoucherApplication> {
    return this.http.get<VoucherApplication>(environment.privateApi + 'Voucher/Application/' + applicationId, { withCredentials: true });
  }

  getParameterResultById(parameterResultId: number): Observable<ParameterResultLetter> {
    return this.http.get<ParameterResultLetter>(environment.privateApi + 'Voucher/ParameterResult/' + parameterResultId, { withCredentials: true });
  }

  getParameterResultsBySampleId(sampleId: number): Observable<ParameterResultLetter> {
    return this.http.get<ParameterResultLetter>(environment.privateApi + 'Voucher/ParameterResultsBySampleId/' + sampleId, { withCredentials: true });
  }

  saveVoucher(voucher: Voucher): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/SaveVoucher', voucher, httpOptions);
  }

  saveVoucherApplication(application: VoucherApplication): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/SaveApplication', application, httpOptions);
  }

  getVoucherLetters(voucherId: number): Observable<VoucherLetter[]> {
    return this.http.get<VoucherLetter[]>(environment.privateApi + 'Voucher/Letters/' + voucherId, { withCredentials: true });
  }

  getVoucherApplicationLetters(applicationId: number): Observable<VoucherLetter[]> {
    return this.http.get<VoucherLetter[]>(environment.privateApi + 'Voucher/ApplicationLetters/' + applicationId, { withCredentials: true });
  }

  uploadLetter(letter: VoucherLetterUpload): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/UploadLetter', letter, httpOptions);
  }

  uploadApplicationLetter(letter: VoucherLetterUpload): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/UploadApplicationLetter', letter, httpOptions);
  }

  attachLetter(file: FormData): Observable<any> {
    return this.http.post<any>(environment.privateApi + 'Voucher/AttachLetter', file, httpOptionsForFiles);
  }

  deleteVoucherLetter(id: number): Observable<any> {
    return this.http.get(environment.privateApi + 'Voucher/DeleteLetter/' + id, { withCredentials: true });
  }

  deleteVoucherApplicationLetter(id: number): Observable<any> {
    return this.http.get(environment.privateApi + 'Voucher/DeleteApplicationLetter/' + id, { withCredentials: true });
  }

  downloadVoucherLetter(id: number): Observable<Blob> {
    return this.http.get<Blob>(environment.privateApi + 'Voucher/DownloadLetter/' + id, { responseType: 'blob' as 'json', withCredentials: true });
  }

  downloadApplicationLetter(id: number): Observable<Blob> {
    return this.http.get<Blob>(environment.privateApi + 'Voucher/DownloadApplicationLetter/' + id, { responseType: 'blob' as 'json', withCredentials: true });
  }

  merge(id: number, ids: number[]): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/MergeVouchers/' + id, ids, httpOptions);
  }

  updateLetterInfo(letter: VoucherLetter): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/UpdateLetterInfo/', letter, httpOptions);
  }

  emailLetter(test: VoucherLetterEmailViewModel): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/EmailLetter/', test, httpOptions);
  }

  emailApplicationLetter(test: VoucherLetterEmailViewModel): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/EmailApplicationLetter/', test, httpOptions);
  }

  getNitrateExceedanceReport(): Observable<VoucherReportNitrateExceedance[]> {
    return this.http.get<VoucherReportNitrateExceedance[]>(environment.privateApi + 'Voucher/Report/NitrateExceedance', httpOptions);
  }

  getVoucherExceedanceNotIndicatedReport(): Observable<VoucherReport[]> {
    return this.http.get<VoucherReport[]>(environment.privateApi + 'Voucher/Report/VoucherExceedanceSamplesNotIndicated', httpOptions);
  }

  getMissingLatLongReport(): Observable<ISearchResult[]> {
    return this.http.get<ISearchResult[]>(environment.privateApi + 'Voucher/Report/MissingLatLong', httpOptions);
  }

  lookupWellLatLong(wellId): Observable<any> {
    return this.http.get<any>(environment.privateApi + 'Voucher/LookupLatLong/' + wellId, httpOptions);
  }

  getVoucherLetterTasks(): Observable<VoucherLetter[]> {
    return this.http.get<VoucherLetter[]>(environment.privateApi + 'Voucher/LetterTasks/', httpOptions);
  }

  // #endregion

  // #region Bulk Upload

  GetAnalyticalMethods(): Observable<any> {
    return this.http.get(environment.privateApi + 'Voucher/GetAnalyticalMethods', { withCredentials: true });
  }

  GetVoucherIds(): Observable<any> {
    return this.http.get(environment.privateApi + 'Voucher/GetVoucherIds', { withCredentials: true });
  }

  VerifyBulkUploadSamples(bulkUploadSample: IBulkUploadSample[] = []): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/VerifyBulkUploadSamples', bulkUploadSample, httpOptions);
  }

  SaveBulkUpload(bulkUploadSamples: IBulkUploadSample[] = []): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/SaveBulkUpload', bulkUploadSamples, httpOptions);
  }

  loadResponses(voucherType: string): Observable<VoucherApplication[]> {
    return this.http.get<VoucherApplication[]>(environment.privateApi + 'Voucher/GetVoucherResponses/' + voucherType, httpOptions);
  }

  importResponse(application: VoucherApplication): Observable<any> {
    return this.http.post(environment.privateApi + 'Voucher/ImportResponse/', application, httpOptions);
  }
  // #endregion

  // #region Helpers

  getCounties(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/county', { withCredentials: true });
  }

  onHttpError(error: any) {
    console.log('error: ', error);
    this.errorMessages = [];
console.log('1');
    Object.keys(error.error.errors).forEach(key => {
      this.errorMessages.push(error.error.errors[key][0]);
    });

    if(this.errorMessages.length == 0 && error.error != null && error.error != undefined) {
      this.errorMessages.push(error.error);
    }

    if (this.errorMessages.length == 0) {
      this.errorMessages.push('An unknown error occurred. Please try again later.');
    }

    console.log('error details: ' + this.errorMessages);

    return this.errorMessages;
  }

  // #endregion

  getAllIssuedVouchers(voucherType: string): Observable<IVoucherSearchResult[]> {
    if (voucherType == null || voucherType == undefined || voucherType.length == 0) {
      return this.http.get<IVoucherSearchResult[]>(environment.privateApi + 'Voucher/GetAllIssuedVouchers', httpOptions);
    }
    return this.http.get<IVoucherSearchResult[]>(environment.privateApi + 'Voucher/GetAllIssuedVouchersByType/' + voucherType, httpOptions);
  }

  getTreatmentPlansByVoucherIds(voucherIds): Observable<ITreatmentViewModel[]> {
    return this.http.post<ITreatmentViewModel[]>(environment.privateApi + 'Voucher/GetTreatmentPlansByVoucherIds', voucherIds, httpOptions);
  }

  getSamplesByTreatementId(treatmentId: number): Observable<SampleViewModel[]> {
    return this.http.get<SampleViewModel[]>(environment.privateApi + 'Voucher/GetSamplesByTreatementId/' + treatmentId, httpOptions);
  }
}